import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@localagent/ui/components/button';
import { Badge } from '@localagent/ui/components/badge';
import { Input } from '@localagent/ui/components/input';
import { useCatalog, useConnections } from '../hooks/useIntegrations';
import { ConnectionStatusBadge } from '../components/integrations/ConnectionStatusBadge';
import { HealthStatusIndicator } from '../components/integrations/HealthStatusIndicator';
import { ConnectDialog } from '../components/integrations/ConnectDialog';
import { IntegrationLogo } from '../components/integrations/IntegrationLogo';
import type { CatalogEntry } from '../types/integrations-types';
import { PageShell } from '../components/layout/PageShell';
import { useCatalogSubscription } from '../hooks/useCatalogSubscription';
import { LoadingState } from '@localagent/ui/components/shogun-spinner';

const CATEGORIES = [
  { value: '', label: 'All' },
  { value: 'developer_tools', label: 'Developer Tools' },
  { value: 'communication', label: 'Communication' },
  { value: 'project_management', label: 'Project Management' },
  { value: 'crm', label: 'CRM' },
  { value: 'cloud_infrastructure', label: 'Cloud' },
  { value: 'analytics', label: 'Analytics' },
  { value: 'storage', label: 'Storage' },
  { value: 'ai_ml', label: 'AI/ML' },
  { value: 'custom', label: 'Custom' },
];

type Tab = 'connections' | 'catalog';

export function IntegrationsView() {
  useCatalogSubscription('integrations');
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>('connections');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [connectEntry, setConnectEntry] = useState<CatalogEntry | null>(null);

  const { data: connections, isLoading: connectionsLoading } = useConnections();
  const { data: catalogForLogos } = useCatalog();
  const definitionById = useMemo(() => {
    const map = new Map<string, CatalogEntry['definition']>();
    for (const entry of catalogForLogos?.entries ?? []) {
      map.set(entry.definition.id, entry.definition);
    }
    return map;
  }, [catalogForLogos]);
  const catalogParams = useMemo(() => {
    const p: { q?: string; category?: string } = {};
    if (searchQuery) p.q = searchQuery;
    if (selectedCategory) p.category = selectedCategory;
    return p;
  }, [searchQuery, selectedCategory]);
  const { data: catalog, isLoading: catalogLoading } = useCatalog(catalogParams);

  const activeConnections = connections?.filter(c => c.status === 'active') ?? [];
  const issueConnections = connections?.filter(c => ['expired', 'auth_failed'].includes(c.status)) ?? [];

  const statusSummary =
    activeConnections.length > 0 ? (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="inline-block h-2 w-2 rounded-full bg-green-500" />
        {activeConnections.length} active
        {issueConnections.length > 0 && (
          <>
            <span className="mx-1">|</span>
            <span className="inline-block h-2 w-2 rounded-full bg-red-500" />
            {issueConnections.length} needs attention
          </>
        )}
      </div>
    ) : null;

  const tabs = (
        <div className="flex gap-1">
          <button
            onClick={() => setTab('connections')}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${tab === 'connections' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`}
          >
            My Connections
            {connections && connections.length > 0 && (
              <span className="ml-1.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-foreground/20 px-1 text-xs">{connections.length}</span>
            )}
          </button>
          <button
            onClick={() => setTab('catalog')}
            className={`rounded-md px-4 py-2 text-sm font-medium transition-colors ${tab === 'catalog' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent'}`}
          >
            Catalog
          </button>
        </div>
  );

  return (
    <PageShell
      title="Integrations"
      description="Connect third-party services to your agent"
      headerActions={statusSummary}
      headerSlot={tabs}
    >
      <>
        {tab === 'connections' && (
          <ConnectionsTab
            connections={connections ?? []}
            isLoading={connectionsLoading}
            definitionById={definitionById}
            onViewConnection={(id) => void navigate(`/integrations/connections/${id}`)}
            onBrowseCatalog={() => setTab('catalog')}
          />
        )}

        {tab === 'catalog' && (
          <CatalogTab
            catalog={catalog}
            isLoading={catalogLoading}
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            selectedCategory={selectedCategory}
            onCategoryChange={setSelectedCategory}
            onViewDetail={(slug) => void navigate(`/integrations/catalog/${slug}`)}
            onConnect={setConnectEntry}
          />
        )}

      {connectEntry && (
        <ConnectDialog
          definitionId={connectEntry.definition.id}
          definitionName={connectEntry.definition.name}
          authType={connectEntry.definition.auth_type}
          open={true}
          onOpenChange={(open) => { if (!open) setConnectEntry(null); }}
          onSuccess={(connectionId) => {
            setConnectEntry(null);
            void navigate(`/integrations/connections/${connectionId}`);
          }}
        />
      )}
      </>
    </PageShell>
  );
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface ConnectionsTabProps {
  connections: Array<{
    id: string;
    name: string;
    status: string;
    last_health_status: string | null;
    definition_id: string;
    created_at?: string;
  }>;
  isLoading: boolean;
  definitionById: Map<string, CatalogEntry['definition']>;
  onViewConnection: (id: string) => void;
  onBrowseCatalog: () => void;
}

function ConnectionsTab({ connections, isLoading, definitionById, onViewConnection, onBrowseCatalog }: ConnectionsTabProps) {
  if (isLoading) {
    return <LoadingState label="Loading connections..." className="py-12" />;
  }

  if (connections.length === 0) {
    return (
      <div className="mx-auto max-w-md py-16 text-center">
        <h2 className="text-lg font-semibold">No integrations connected</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          Connect third-party services like GitHub, Slack, or Jira so your agent can work with them.
        </p>
        <Button className="mt-6" onClick={onBrowseCatalog}>
          Browse Catalog
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-3">
      {connections.map((conn) => (
        <div
          key={conn.id}
          onClick={() => onViewConnection(conn.id)}
          className="cursor-pointer rounded-lg p-4 transition-colors hover:bg-accent"
          data-testid={`connection-item-${conn.id}`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <HealthStatusIndicator status={conn.last_health_status} />
              <IntegrationLogo
                slug={definitionById.get(conn.definition_id)?.slug}
                name={definitionById.get(conn.definition_id)?.name ?? conn.name}
                iconUrl={definitionById.get(conn.definition_id)?.icon_url}
                size={32}
              />
              <div>
                <h3 className="font-medium">{conn.name}</h3>
                {conn.created_at && (
                  <p className="text-xs text-muted-foreground">Connected {new Date(conn.created_at).toLocaleDateString()}</p>
                )}
              </div>
            </div>
            <ConnectionStatusBadge status={conn.status} />
          </div>
        </div>
      ))}
      <div className="pt-4 text-center">
        <Button variant="outline" onClick={onBrowseCatalog}>
          + Add Integration
        </Button>
      </div>
    </div>
  );
}

interface CatalogTabProps {
  catalog: { entries: CatalogEntry[]; total_count: number } | undefined;
  isLoading: boolean;
  searchQuery: string;
  onSearchChange: (q: string) => void;
  selectedCategory: string;
  onCategoryChange: (c: string) => void;
  onViewDetail: (slug: string) => void;
  onConnect: (entry: CatalogEntry) => void;
}

function CatalogTab({ catalog, isLoading, searchQuery, onSearchChange, selectedCategory, onCategoryChange, onViewDetail, onConnect }: CatalogTabProps) {
  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <Input
          placeholder="Search integrations..."
          value={searchQuery}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onSearchChange(e.target.value)}
          className="max-w-sm"
          data-testid="catalog-search"
        />
        <div className="flex flex-wrap gap-1">
          {CATEGORIES.map((cat) => (
            <button
              key={cat.value}
              onClick={() => onCategoryChange(cat.value)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${selectedCategory === cat.value ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-accent'}`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      {isLoading && (
        <LoadingState label="Loading catalog..." className="py-12" />
      )}

      {!isLoading && catalog && catalog.entries.length === 0 && (
        <div className="py-12 text-center text-sm text-muted-foreground">
          No integrations found{searchQuery ? ` for "${searchQuery}"` : ''}.
        </div>
      )}

      {!isLoading && catalog && catalog.entries.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {catalog.entries.map((entry) => (
            <div
              key={entry.definition.slug}
              className="group cursor-pointer rounded-lg bg-card p-4 transition-colors hover:bg-accent"
              onClick={() => onViewDetail(entry.definition.slug)}
              data-testid={`catalog-card-${entry.definition.slug}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <IntegrationLogo
                    slug={entry.definition.slug}
                    name={entry.definition.name}
                    iconUrl={entry.definition.icon_url}
                    size={40}
                  />
                  <div>
                    <h3 className="font-medium">{entry.definition.name}</h3>
                    <p className="text-xs text-muted-foreground">{entry.definition.category.replace(/_/g, ' ')}</p>
                  </div>
                </div>
                {entry.has_active_connection && (
                  <Badge variant="success" className="text-xs">Connected</Badge>
                )}
              </div>
              {entry.definition.description && (
                <p className="mt-3 line-clamp-2 text-sm text-muted-foreground">{entry.definition.description}</p>
              )}
              <div className="mt-3 flex items-center justify-between">
                <Badge variant="outline" className="text-xs">{entry.definition.auth_type.replace(/_/g, ' ')}</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  className="opacity-0 transition-opacity group-hover:opacity-100"
                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); onConnect(entry); }}
                >
                  Connect
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
